import React, { Fragment, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { useAlert } from 'react-alert';
import Loader from '../../layout/loader';
import Navbar from '../HomeScreen/Navbar';
import MetaTag from '../../layout/MetaTag';
import { Helmet } from 'react-helmet';
import { myOrderAction } from '../../redux/actions/orderAction';
import { userLogoutAction } from '../../redux/actions/userAction';
import './userDetail.css';

// Material Icons
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import SettingsIcon from '@material-ui/icons/Settings';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import CancelIcon from '@mui/icons-material/Cancel';
import BrowserUpdatedIcon from '@mui/icons-material/BrowserUpdated';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import DashboardIcon from '@material-ui/icons/Dashboard';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

function UserProfile() {
  const history = useHistory();
  const dispatch = useDispatch();
  const alert = useAlert();

  // Redux state
  const { user, loading, isAuthenticated } = useSelector((state) => state.user);
  const { wishListProducts } = useSelector((state) => state.wishListAll);
  const { carts } = useSelector((state) => state.carts);
  const { orders } = useSelector((state) => state.myOrders);

  useEffect(() => {
    if (isAuthenticated === false) {
      history.push('/Login');
    } else {
      dispatch(myOrderAction());
    }
  }, [dispatch, history, isAuthenticated]);

  const profileName = useMemo(() => {
    return `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Valued Customer';
  }, [user]);

  const initials = useMemo(() => {
    return (
      profileName
        .split(/\s+/)
        .slice(0, 2)
        .map((name) => name.charAt(0))
        .join('')
        .toUpperCase() || 'U'
    );
  }, [profileName]);

  const joinedDate = user?.date
    ? new Date(user.date).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    : 'Recently';

  const orderCount = orders ? orders.length : 0;
  const wishlistCount = wishListProducts ? wishListProducts.length : 0;
  const cartCount = carts ? carts.length : 0;

  const handleLogout = () => {
    dispatch(userLogoutAction());
    if (alert) alert.success('Logged out successfully');
    history.push('/Login');
  };

  const accountDetails = [
    { label: 'Full Name', value: profileName, icon: <PersonOutlineIcon /> },
    { label: 'Email Address', value: user?.email || 'Not available', icon: <EmailOutlinedIcon /> },
    { label: 'Mobile Number', value: user?.number || 'Not available', icon: <PhoneIphoneOutlinedIcon /> },
    { label: 'Gender', value: user?.gender || 'Not specified', icon: <BadgeOutlinedIcon /> },
    { label: 'Member Since', value: joinedDate, icon: <CalendarMonthOutlinedIcon /> }
  ];

  const orderActions = [
    {
      title: 'My Orders',
      subtitle: `${orderCount} order${orderCount === 1 ? '' : 's'} • Invoices, delivery & receipts`,
      icon: <ShoppingBagOutlinedIcon />,
      to: '/orders/Details/me',
      badge: orderCount > 0 ? `${orderCount}` : null
    },
    {
      title: 'Track Live Order',
      subtitle: 'Real-time courier GPS tracking via Shiprocket',
      icon: <LocalShippingOutlinedIcon />,
      to: 'https://im.shiprocket.co/',
      external: true
    },
    {
      title: 'Returns & Exchange',
      subtitle: 'Manage returns, replacements & doorstep pickups',
      icon: <ViewInArIcon />,
      to: '/user/All/return/orders'
    },
    {
      title: 'Cancelled Orders',
      subtitle: 'Check cancellation status & refund progress',
      icon: <CancelIcon />,
      to: '/profile/Cancel-Orders-Detail'
    },
    {
      title: 'Refund Bank Details',
      subtitle: 'Add or update bank account for direct refund payouts',
      icon: <BrowserUpdatedIcon />,
      to: '/user-profile/add-bank-account/'
    }
  ];

  const settingsActions = [
    {
      title: 'Edit Profile',
      subtitle: 'Update your name, contact info & preferences',
      icon: <SettingsIcon />,
      to: '/UpdateProfile'
    },
    {
      title: 'Change Password',
      subtitle: 'Keep your account secure with regular updates',
      icon: <LockOpenIcon />,
      to: '/UpdatePassword'
    },
    {
      title: 'Help & Complaints',
      subtitle: 'Raise issues, queries or reach customer support',
      icon: <SupportAgentIcon />,
      to: '/user/complain/request'
    },
    {
      title: 'Notifications & Offers',
      subtitle: 'Stay updated on flash discounts & local deals',
      icon: <NotificationsActiveOutlinedIcon />,
      to: '/notification'
    }
  ];

  if (user?.role === 'admin') {
    settingsActions.unshift({
      title: 'Admin Dashboard',
      subtitle: 'Manage sellers, orders, products & platform',
      icon: <DashboardIcon />,
      to: '/Admin/dashboard',
      highlight: true
    });
  }

  return (
    <Fragment>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=2.0" />
        <meta name="description" content="Manage your orders, profile, wishlist, and account security on IM Online Shopping." />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`My Account | IM Online Shopping`} />
        <meta property="og:description" content="Manage your orders, addresses, wishlist and security on IM Online Shopping." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Global Navbar with Desktop Bar + Mobile Top Header + Mobile Bottom Navigation */}
      <Navbar />

      {loading ? (
        <Loader />
      ) : (
        <Fragment>
          <MetaTag title={`My Account - ${profileName} | IM Online Shopping`} />

          <main className="im-profile-page">
            <div className="im-profile-container">
              {/* Profile Hero Header */}
              <section className="im-profile-hero" aria-label="Profile Header">
                <div className="im-profile-hero-left">
                  <div className="im-profile-avatar-wrap">
                    <div className="im-profile-avatar">{initials}</div>
                    <span className="im-profile-status-dot" title="Account active" />
                  </div>
                  <div className="im-profile-hero-info">
                    <div className="im-profile-badge-row">
                      <span className="im-profile-role-badge">
                        <CheckCircleIcon className="im-badge-icon" />
                        {user?.role === 'admin' ? 'Admin / Seller' : 'Verified Member'}
                      </span>
                      <span className="im-profile-joined-chip">Since {joinedDate}</span>
                    </div>
                    <h1 className="im-profile-name">{profileName}</h1>
                    <div className="im-profile-contact-meta">
                      {user?.email && (
                        <span className="im-meta-pill">
                          <EmailOutlinedIcon /> {user.email}
                        </span>
                      )}
                      {user?.number && (
                        <span className="im-meta-pill">
                          <PhoneIphoneOutlinedIcon /> {user.number}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="im-profile-hero-actions">
                  <Link to="/UpdateProfile" className="im-btn-hero secondary">
                    <SettingsIcon className="btn-icon" />
                    <span>Edit Profile</span>
                  </Link>
                  <button onClick={handleLogout} className="im-btn-hero danger" aria-label="Sign out">
                    <ExitToAppIcon className="btn-icon" />
                    <span>Log Out</span>
                  </button>
                </div>
              </section>

              {/* Quick Stats Ribbon */}
              <section className="im-profile-stats" aria-label="Account Overview">
                <Link to="/orders/Details/me" className="im-stat-item">
                  <div className="im-stat-icon-wrap orders">
                    <ShoppingBagOutlinedIcon />
                  </div>
                  <div className="im-stat-content">
                    <span className="im-stat-val">{orderCount}</span>
                    <span className="im-stat-lbl">My Orders</span>
                  </div>
                  <ArrowForwardIosIcon className="im-stat-chevron" />
                </Link>

                <Link to="/user/wishlist" className="im-stat-item">
                  <div className="im-stat-icon-wrap wishlist">
                    <FavoriteBorderIcon />
                  </div>
                  <div className="im-stat-content">
                    <span className="im-stat-val">{wishlistCount}</span>
                    <span className="im-stat-lbl">Saved Items</span>
                  </div>
                  <ArrowForwardIosIcon className="im-stat-chevron" />
                </Link>

                <Link to="/Cart" className="im-stat-item">
                  <div className="im-stat-icon-wrap cart">
                    <ShoppingCartOutlinedIcon />
                  </div>
                  <div className="im-stat-content">
                    <span className="im-stat-val">{cartCount}</span>
                    <span className="im-stat-lbl">In Cart</span>
                  </div>
                  <ArrowForwardIosIcon className="im-stat-chevron" />
                </Link>

                <div className="im-stat-item static">
                  <div className="im-stat-icon-wrap secure">
                    <ShieldOutlinedIcon />
                  </div>
                  <div className="im-stat-content">
                    <span className="im-stat-val green">Active</span>
                    <span className="im-stat-lbl">Protection</span>
                  </div>
                </div>
              </section>

              {/* Main Dashboard Grid */}
              <div className="im-profile-grid">
                {/* Left/Main Column: Actions */}
                <div className="im-profile-main-col">
                  {/* Orders & Fulfillment Group */}
                  <div className="im-profile-section-card">
                    <div className="im-section-card-header">
                      <div>
                        <h2 className="im-section-title">Orders & Deliveries</h2>
                        <p className="im-section-subtitle">Track, return, or cancel purchases</p>
                      </div>
                      <Link to="/orders/Details/me" className="im-section-link">
                        View All
                      </Link>
                    </div>

                    <div className="im-actions-list">
                      {orderActions.map((item) => {
                        const inner = (
                          <Fragment>
                            <div className="im-action-icon-box">{item.icon}</div>
                            <div className="im-action-text">
                              <div className="im-action-title-row">
                                <span className="im-action-title">{item.title}</span>
                                {item.badge && <span className="im-action-badge">{item.badge}</span>}
                              </div>
                              <span className="im-action-desc">{item.subtitle}</span>
                            </div>
                            {item.external ? (
                              <OpenInNewIcon className="im-action-arrow" />
                            ) : (
                              <ArrowForwardIosIcon className="im-action-arrow" />
                            )}
                          </Fragment>
                        );

                        return item.external ? (
                          <a
                            key={item.title}
                            href={item.to}
                            target="_blank"
                            rel="noreferrer"
                            className="im-action-row"
                          >
                            {inner}
                          </a>
                        ) : (
                          <Link key={item.title} to={item.to} className="im-action-row">
                            {inner}
                          </Link>
                        );
                      })}
                    </div>
                  </div>

                  {/* Settings & Security Group */}
                  <div className="im-profile-section-card">
                    <div className="im-section-card-header">
                      <div>
                        <h2 className="im-section-title">Account & Security</h2>
                        <p className="im-section-subtitle">Manage preferences, security & support</p>
                      </div>
                    </div>

                    <div className="im-actions-list">
                      {settingsActions.map((item) => (
                        <Link
                          key={item.title}
                          to={item.to}
                          className={`im-action-row ${item.highlight ? 'im-highlight-row' : ''}`}
                        >
                          <div className={`im-action-icon-box ${item.highlight ? 'admin-icon' : ''}`}>
                            {item.icon}
                          </div>
                          <div className="im-action-text">
                            <span className="im-action-title">{item.title}</span>
                            <span className="im-action-desc">{item.subtitle}</span>
                          </div>
                          <ArrowForwardIosIcon className="im-action-arrow" />
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right/Side Column: Account Details & Guarantees */}
                <div className="im-profile-side-col">
                  {/* Personal Info Card */}
                  <div className="im-profile-side-card">
                    <div className="im-side-card-header">
                      <h3>Personal Details</h3>
                      <Link to="/UpdateProfile" className="im-edit-link">
                        Edit
                      </Link>
                    </div>

                    <div className="im-personal-details-list">
                      {accountDetails.map((detail) => (
                        <div className="im-personal-detail-row" key={detail.label}>
                          <span className="im-detail-icon-pill">{detail.icon}</span>
                          <div className="im-detail-meta">
                            <span className="im-detail-label">{detail.label}</span>
                            <strong className="im-detail-value">{detail.value}</strong>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Buyer Protection / Trust Assurance */}
                  <div className="im-trust-card">
                    <div className="im-trust-card-top">
                      <VerifiedUserOutlinedIcon className="im-trust-shield-icon" />
                      <div>
                        <h4>IM Verified Buyer Protection</h4>
                        <p>Your orders and account are protected end-to-end.</p>
                      </div>
                    </div>
                    <ul className="im-trust-perks">
                      <li>
                        <CheckCircleIcon />
                        <span>Direct fulfillment from verified local shops</span>
                      </li>
                      <li>
                        <CheckCircleIcon />
                        <span>Easy doorstep returns & verified refunds</span>
                      </li>
                      <li>
                        <CheckCircleIcon />
                        <span>256-bit encrypted checkout with live OTP</span>
                      </li>
                    </ul>
                  </div>

                  {/* Mobile Quick Sign Out */}
                  <div className="im-mobile-logout-wrapper">
                    <button onClick={handleLogout} className="im-mobile-logout-btn">
                      <ExitToAppIcon />
                      <span>Log Out of Account</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </Fragment>
      )}
    </Fragment>
  );
}

export default UserProfile;
